import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo, useCallback, useRef } from "react";
// Components
import IconWrapper from "@vahak/core-ui/dist/components/IconWrapper";
import { toast } from "@vahak/core/dist/components/toast/toast";
import ConferenceCallPopup from "../../components/call/ConferenceCallPopup";
import AddToConference from "../../components/call/AddToConference";
// Constants
import { AppContext } from "@vahak/core/dist/app-context";
import { CallInfo as _CallInfo } from "plivo-browser-sdk/managers/callSession";
import { PLIVO_CALL_STATUS_TEXT, PlivoRemoteInfo, PLVIO_CALL_STATE } from "./type";
// Methods
import { extractPhoneNumber } from "@vahak/core-ui/dist/methods/string";
import { playCallTerminatedTone } from "@vahak/core-ui/dist/methods/sound";
// Hooks
import { CallInitiationParams, useCallInitiation } from "../../services/call/useCallInitiation";
import { useToggleModalWithLocalState } from "@vahak/core-ui/dist/hooks/useToggleModalWithLocalState";
import { useSharedPlivoSdk } from "./SharedPlivoSdkProvider";
// Assets
import CallIconMobile from "@vahak/core/dist/icons/Call_CTA_BLUE.svg";
import ConfernceCallIconMobile from "@vahak/core/dist/icons/phone-user.svg";
import { useSearchCompanyByPhoneNumberMutation } from "@vahak/core/dist/_services/auth.adminConsole.service";
import { useConferenceCall, useConferenceCallEnd, useConferenceCallKick } from "../../services/call/useConferenceCall";
import useRealtimeCallInfo from "../../services/call/useRealtimeCallInfo";
// =============================== * =============================== * =============================== //

interface CallPopupContextType {
    startCall: (meta: CallRequiredMeta, callInitiateParams: CallInitiationParams) => void;
    registerCallCta: (meta: CallRequiredMeta, callInitiateParams: CallInitiationParams) => React.ReactNode;
    isReady?: boolean;
    isCallSessionActive?: boolean;
    callCtaStateKey?: string;
}

// ✅ Create Context
const CallPopupContext = createContext<CallPopupContextType | undefined>(undefined);

export interface CallRequiredMeta {
    name: string;
    number: string;
    imgUrl: string;
}

export interface MemberInfo extends Partial<CallRequiredMeta> {
    memberPhoneNumber: string;
    call_uuid: string;
    remote_meta_info?: PlivoRemoteInfo;
}

export interface CallInfo extends Partial<_CallInfo> {
    state?: PLVIO_CALL_STATE;
    callDesc?: string;
    popupActive?: boolean; // show UI
    isMuted?: boolean;
    startTime?: Date;
    endTime?: Date;
    callInitiateParams?: CallInitiationParams;
    //
    isOutgoingCall?: boolean;
    isIncomingCall?: boolean;
    isCallActive?: boolean; // Ui Maybe visible but call is really active and actionable
    isConnected?: boolean; // action taken and call is now connected
    membersInfo?: Record<MemberInfo["memberPhoneNumber"], MemberInfo>;
    mpcUuid?: string;
}

const MAX_ALLOWED_CONFERENCE_MEMBER = 3;

export const MpcClickToCallProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { companyId, phoneNum, route } = useContext(AppContext);
    const { plivoBrowserSdk, isReady } = useSharedPlivoSdk();
    const [callInfo, setCallInfo] = useState({} as CallInfo);
    const getPhoneNumberDetails = useSearchCompanyByPhoneNumberMutation();
    const callIntitate = useCallInitiation();
    const conferenceIntitate = useConferenceCall();
    const conferenceKick = useConferenceCallKick();
    const conferenceEnd = useConferenceCallEnd();
    const conferencePopup = useToggleModalWithLocalState(false);
    const addToConferencePopup = useToggleModalWithLocalState(false);

    const [mpcCallToResume, setMpcCallToResume] = useState("");
    const [pendingConferencePhones, setPendingConferencePhones] = useState<string[]>([]);
    const mpcCallToResumeRef = useRef(mpcCallToResume);
    mpcCallToResumeRef.current = mpcCallToResume;

    const memberList = Object.values(callInfo?.membersInfo || {});

    const normalizeConferencePhone = (phone?: string) => extractPhoneNumber(phone || "") || "";

    const isPhoneInConference = (phone?: string) => {
        const normalizedPhone = normalizeConferencePhone(phone);
        if (!normalizedPhone) {
            return false;
        }

        const isPending = pendingConferencePhones.some(
            (pendingPhone) => normalizeConferencePhone(pendingPhone) === normalizedPhone
        );
        if (isPending) {
            return true;
        }

        return (
            memberList.findIndex(
                (member) => normalizeConferencePhone(member?.memberPhoneNumber || member?.number) === normalizedPhone
            ) >= 0
        );
    };

    const addPendingConferencePhone = (phone: string) => {
        const normalizedPhone = normalizeConferencePhone(phone);
        if (!normalizedPhone) {
            return;
        }

        setPendingConferencePhones((prev) =>
            prev.some((pendingPhone) => normalizeConferencePhone(pendingPhone) === normalizedPhone)
                ? prev
                : [...prev, phone]
        );
    };

    const removePendingConferencePhone = (phone: string) => {
        const normalizedPhone = normalizeConferencePhone(phone);
        if (!normalizedPhone) {
            return;
        }

        setPendingConferencePhones((prev) =>
            prev.filter((pendingPhone) => normalizeConferencePhone(pendingPhone) !== normalizedPhone)
        );
    };
    const showCallAcceptAction = callInfo?.isCallActive && callInfo?.isIncomingCall && !callInfo?.isConnected;
    const showCallHanguptAction =
        (callInfo?.isIncomingCall ? callInfo?.isConnected : callInfo?.isCallActive) &&
        (!mpcCallToResume ||
            memberList.some((m) =>
                [PLIVO_CALL_STATUS_TEXT.ANSWERED, PLIVO_CALL_STATUS_TEXT.HANGUP].includes(
                    m.remote_meta_info?.current_status!
                )
            ));
    const showCallRejectAction = callInfo?.isIncomingCall && callInfo?.isCallActive && !callInfo?.isConnected;
    const allowMoreParticipant = memberList.length + pendingConferencePhones.length < MAX_ALLOWED_CONFERENCE_MEMBER;
    const showAddToConference = callInfo?.isCallActive && callInfo?.isConnected && allowMoreParticipant;

    const updateCallInfo = (data: Partial<CallInfo>) => setCallInfo((v) => ({ ...v, ...data }));

    // CTA
    const toggleMute = () => {
        setCallInfo((v) => ({ ...v, isMuted: !v.isMuted }));
        callInfo?.isMuted ? plivoBrowserSdk?.client?.unmute() : plivoBrowserSdk?.client?.mute();
    };

    const onHangup = () => {
        console.error("onHangup ~ onHangup CALLED");
        try {
            // onCallTerminated will get triggered
            onEnd();
            // setTimeout(() => {
            //     plivoBrowserSdk?.client?.hangup();
            // }, 1000);
        } catch (error) {
            console.error("onHangup ~ error:", error);
        }
    };
    const onReject = () => {
        plivoBrowserSdk?.client?.reject("");
        // onCallFailed will get triggered
    };
    const onAnswer = (uid?: string) => {
        const _uid = uid || callInfo?.callUUID;
        if (_uid) {
            plivoBrowserSdk?.client?.answer(_uid, "reject");
            //onCallAnswered
        }
    };

    const append91 = (n: string) => {
        const _number = extractPhoneNumber(n || "");
        return _number ? `91${_number}` : "";
    };

    const startCall: CallPopupContextType["startCall"] = async (meta, callInitiateParams) => {
        await addToConference(mpcCallToResumeRef.current, meta, callInitiateParams);
    };

    const reset = (delay: number = 2000) => {
        setMpcCallToResume("");
        setPendingConferencePhones([]);
        setTimeout(() => {
            setCallInfo({ popupActive: false, isCallActive: false, isConnected: false });
        }, delay);
        addToConferencePopup.closeModal();
    };

    const Events = {
        // Outbound Start
        onCalling: (...arr: any) => {
            console.error("onCalling ~ arr:", arr);
            updateCallInfo({
                callDesc: "Connecting",
                popupActive: true,
                isOutgoingCall: true,
                isCallActive: true
            });
        },
        onCallRemoteRinging: (_callInfo: CallInfo) => {
            console.error("onCallRemoteRinging ~ arr:", { _callInfo });
            updateCallInfo({
                ..._callInfo,
                callDesc: "Ringing"
            });
        },
        onCallConnected: (_callInfo: CallInfo) => {
            console.error("onCallConnected ~ arr:", _callInfo);
            updateCallInfo({
                ..._callInfo,
                callDesc: "Connected"
            });
        },
        onCallFailed: (reason: string, _callInfo: CallInfo) => {
            console.error("onCallFailed ", { reason, _callInfo });
            updateCallInfo({
                ..._callInfo,
                callDesc: reason,
                isCallActive: false,
                isConnected: false
            });
            reset();
        },
        onCallAnswered: (_callInfo: CallInfo) => {
            console.error("onCallAnswered ~ arr:", _callInfo);
            updateCallInfo({
                ..._callInfo,
                callDesc: "Answered",
                isConnected: true,
                startTime: new Date()
            });
        },
        onCallTerminated: (hangUpInfo: any, _callInfo: CallInfo) => {
            console.error("onCallTerminated ~ arr:", { hangUpInfo, _callInfo, callInfo });
            updateCallInfo({
                callDesc: "Call Ended",
                isCallActive: false,
                isConnected: false,
                endTime: new Date()
            });
            reset();
            playCallTerminatedTone(2, 200);
        },
        onIncomingCall: (callerID: string, extraHeaders: any, _callInfo: CallInfo, callerName: string) => {
            console.error("onIncomingCall ~ arr:", { callerID, extraHeaders, _callInfo, callerName });

            updateCallInfo({
                ..._callInfo,
                callDesc: "Incoming Call",
                popupActive: true,
                isCallActive: true,
                isIncomingCall: true
            });
            // setTimeout(() => {
            //     onAnswer(_callInfo?.callUUID);
            // }, 1000);
        },
        onIncomingCallCanceled: (...arr: any) => {
            console.error("onIncomingCallCanceled ~ arr:", arr);
            updateCallInfo({
                callDesc: "Incoming Call Cancelled By User",
                isCallActive: false,
                isConnected: false
            });
            reset();
        }
    };

    useRealtimeCallInfo({
        enabled: !!mpcCallToResume && !!callInfo?.popupActive,
        mpcName: mpcCallToResume,
        onUpdate(data) {
            if (data?.length > 0) {
                setCallInfo((prev) => {
                    const updatedMembersInfo = { ...(prev.membersInfo || {}) };
                    data.forEach((member) => {
                        const memberPhn = member.initiation_json?.To;
                        if (!memberPhn || !updatedMembersInfo[memberPhn]) return;
                        if (member.current_status === PLIVO_CALL_STATUS_TEXT.HANGUP) {
                            if (data?.length === 2) {
                                onHangup();
                            } else {
                                delete updatedMembersInfo[memberPhn];
                            }
                            return;
                        }
                        updatedMembersInfo[memberPhn].remote_meta_info = member;
                    });
                    return {
                        ...prev,
                        membersInfo: updatedMembersInfo
                    };
                });
            }
        }
    });

    useEffect(() => {
        if (!plivoBrowserSdk) {
            return;
        }

        const client = plivoBrowserSdk.client;
        const callEvents: Array<[string, (...args: any[]) => void]> = [
            ["onCalling", Events.onCalling],
            ["onCallConnected", Events.onCallConnected],
            ["onCallAnswered", Events.onCallAnswered],
            ["onCallRemoteRinging", Events.onCallRemoteRinging],
            ["onCallFailed", Events.onCallFailed],
            ["onCallTerminated", Events.onCallTerminated],
            ["onIncomingCall", Events.onIncomingCall],
            ["onIncomingCallCanceled", Events.onIncomingCallCanceled]
        ];

        callEvents.forEach(([event, handler]) => client.on(event, handler));

        return () => {
            callEvents.forEach(([event, handler]) => client.off(event, handler));
        };
    }, [plivoBrowserSdk, companyId]);

    const registerCallCta: CallPopupContextType["registerCallCta"] = (meta, callInitiateParams) => {
        if (isPhoneInConference(meta?.number)) {
            return null;
        }

        const activeMpcName = mpcCallToResumeRef.current;
        const isConferenceSessionActive = Boolean(
            activeMpcName || callInfo?.popupActive || callInfo?.isCallActive || pendingConferencePhones.length
        );

        if (isReady && meta?.number && allowMoreParticipant) {
            if (isConferenceSessionActive) {
                if (!activeMpcName) {
                    return null;
                }

                return (
                    <IconWrapper
                        width={32}
                        height={32}
                        onClick={() => addToConference(activeMpcName, meta, callInitiateParams)}
                    >
                        <ConfernceCallIconMobile />
                    </IconWrapper>
                );
            }

            return (
                <IconWrapper onClick={() => startCall(meta, callInitiateParams)}>
                    <CallIconMobile />
                </IconWrapper>
            );
        }

        return null;
    };

    const addToConference = (mpcName: string, meta: CallRequiredMeta, callInitiateParams: CallInitiationParams) => {
        return new Promise((resolve) => {
            const { number } = meta || {};
            const reqNumber = append91(number || "");
            const agentNumber = append91(String(phoneNum) || "");

            if (!reqNumber) {
                return resolve(0);
            }

            addPendingConferencePhone(reqNumber);
            updateCallInfo({
                popupActive: true,
                isOutgoingCall: true,
                isCallActive: true,
                callDesc: mpcName ? "Adding to conference" : "Connecting"
            });

            conferenceIntitate.mutateAsync(
                {
                    screen_name: route,
                    ...callInitiateParams,
                    ...(mpcName
                        ? {
                              mpc_name: mpcName
                          }
                        : {
                              agent: agentNumber
                          }),
                    customers: [reqNumber]
                },
                {
                    onSuccess(data) {
                        const resolvedMpcName = data?.data?.mpc_name || mpcName;
                        if (resolvedMpcName) {
                            setMpcCallToResume(resolvedMpcName);
                        }

                        const memberInfo = data?.data?.users?.customers?.find((member) => reqNumber === member?.phone);

                        if (memberInfo) {
                            setCallInfo((i) => ({
                                ...i,
                                mpcUuid: data?.data?.mpc_uuid,
                                membersInfo: {
                                    ...i["membersInfo"],
                                    [memberInfo.phone]: {
                                        ...meta,
                                        call_uuid: memberInfo.call_uuid,
                                        memberPhoneNumber: memberInfo.phone
                                    }
                                }
                            }));
                        }

                        removePendingConferencePhone(reqNumber);
                        resolve(0);
                    },
                    onError: (err) => {
                        removePendingConferencePhone(reqNumber);
                        err?.message && toast.error("Unable to add");
                    }
                }
            );
        });
    };

    const onSubmitAddConferenceCall = async (meta: CallRequiredMeta) => {
        if (isPhoneInConference(meta?.number)) {
            return toast.error("Already In Call");
        }
        addToConferencePopup?.toggleModal();
        await addToConference(mpcCallToResumeRef.current, meta, {});
    };

    const onKick = (cuid: string) => {
        conferenceKick.mutateAsync(
            {
                call_uuid: cuid,
                mpc_name: mpcCallToResume!
            },
            {
                onSuccess(data) {
                    const memberInfo = memberList?.find((member) => cuid === member?.call_uuid);
                    if (!memberInfo) return;
                    setCallInfo((prev) => {
                        const { [memberInfo.memberPhoneNumber]: _, ...restMembers } = prev.membersInfo || {};
                        return {
                            ...prev,
                            membersInfo: restMembers
                        };
                    });
                },
                onError: (err) => {
                    err?.message && toast.error("You cannot remove the user");
                }
            }
        );
    };
    const onEnd = () => {
        conferenceEnd.mutateAsync(
            {
                mpc_name: mpcCallToResume
            },
            {
                onSuccess(data) {},
                onError: (err) => {
                    err?.message && toast.error("Error while end");
                }
            }
        );
    };

    const startCallRef = useRef(startCall);
    const registerCallCtaRef = useRef(registerCallCta);
    startCallRef.current = startCall;
    registerCallCtaRef.current = registerCallCta;

    const stableStartCall = useCallback<CallPopupContextType["startCall"]>(
        (meta, callInitiateParams) => startCallRef.current(meta, callInitiateParams),
        []
    );
    const stableRegisterCallCta = useCallback<CallPopupContextType["registerCallCta"]>(
        (meta, callInitiateParams) => registerCallCtaRef.current(meta, callInitiateParams),
        []
    );

    const contextValue = useMemo(
        () => ({
            startCall: stableStartCall,
            registerCallCta: stableRegisterCallCta,
            isReady,
            isCallSessionActive: Boolean(
                callInfo?.popupActive || callInfo?.isCallActive || mpcCallToResume || pendingConferencePhones.length
            ),
            callCtaStateKey: [
                mpcCallToResume,
                ...memberList.map((member) => normalizeConferencePhone(member.memberPhoneNumber || member.number)),
                ...pendingConferencePhones.map((phone) => normalizeConferencePhone(phone)),
                String(allowMoreParticipant),
                String(callInfo?.popupActive)
            ].join("|")
        }),
        [
            isReady,
            callInfo?.popupActive,
            callInfo?.isCallActive,
            mpcCallToResume,
            memberList,
            pendingConferencePhones,
            allowMoreParticipant,
            stableStartCall,
            stableRegisterCallCta
        ]
    );

    return (
        <>
            {isReady && !!callInfo?.popupActive && (
                <ConferenceCallPopup
                    onToggleMute={toggleMute}
                    callInfo={callInfo}
                    {...(showCallAcceptAction && {
                        onAnswer
                    })}
                    {...(showCallHanguptAction && {
                        onHangup,
                        onKick
                    })}
                    // {...(showCallRejectAction && {
                    //     onReject
                    // })}
                    {...(showAddToConference && {
                        onClickAddToConference: addToConferencePopup?.toggleModal
                    })}
                />
            )}

            {addToConferencePopup?.isModalOpen && (
                <AddToConference
                    onSubmit={onSubmitAddConferenceCall}
                    popupProps={{ onToggle: addToConferencePopup?.toggleModal }}
                />
            )}

            <CallPopupContext.Provider value={contextValue}>{children}</CallPopupContext.Provider>
        </>
    );
};

// ✅ Custom Hook for Using Context
export const useMpcClickToCall = (): CallPopupContextType | undefined => {
    const context = useContext(CallPopupContext);
    return context;
};
